import type { Access, Visibility } from '@photos/shared';
import type { IconName } from '../components/Icon';

export interface VisibilityLook {
  icon: IconName;
  /** CSS color: a variable Studio's theme overrides (status colors), with the Photos color as fallback. */
  color: string;
  label: string;
  /** Who can see it, for dialogs and help text. */
  description: string;
  /** Small badge on grid tiles (released photos have none). */
  badge?: { icon: IconName; label: string };
}

export const VISIBILITY_LOOK: Record<Visibility, VisibilityLook> = {
  unreleased: {
    icon: 'eyeOff',
    color: 'var(--vis-unreleased, #7244b8)',
    label: 'Unreleased',
    description: 'Only in Studio. New uploads start here until they’re reviewed and released.',
    badge: { icon: 'eyeOff', label: 'Unreleased' },
  },
  released: {
    icon: 'globe',
    color: 'var(--vis-released, #2b5bd0)',
    label: 'Released',
    description: 'Everyone signed in can see these photos.',
  },
  restricted: {
    icon: 'lock',
    color: 'var(--vis-restricted, #b33a5d)',
    label: 'Restricted',
    description: 'Only people allowed to see restricted photos (e.g. church school). Never shareable by link.',
    badge: { icon: 'lock', label: 'Restricted' },
  },
};

export const VISIBILITY_ORDER: Visibility[] = ['unreleased', 'released', 'restricted'];

/** Why `access` can't move every photo in `from` to `to`, or null when it can. */
export function visibilityBlocker(access: Access, from: readonly Visibility[], to: Visibility): string | null {
  if (from.every((v) => access.canSetVisibility(v, to))) return null;
  if (to === 'restricted' || from.includes('restricted')) {
    if (!access.seesRestricted) return 'Needs permission to see restricted photos';
    if (!access.isMedia) return 'Needs Media';
  }
  return 'Needs Can release';
}

/** Toast after changing the visibility of `n` photos. */
export function visibilityToast(to: Visibility, n: number): { icon: IconName; title: string; sub: string } {
  const noun = n === 1 ? 'photo' : 'photos';
  if (to === 'released') return { icon: 'globe', title: `Released ${n} ${noun}`, sub: 'Everyone signed in can now see them.' };
  if (to === 'unreleased')
    return { icon: 'eyeOff', title: `Unreleased ${n} ${noun}`, sub: 'They’re only in Studio now.' };
  return { icon: 'lock', title: `Restricted ${n} ${noun}`, sub: 'Only people allowed to see restricted photos can see them.' };
}
